import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { withdrawalService } from '../../../services/dashboardService';
import { cleanDocument, formatCpfCnpjInput } from '../../../utils/cpfCnpjValidator';
import {
  inferRegisterTypeFromDocument,
  looksLikePersonName,
  resolveCorporateHolderName,
  suggestCompanyHolderName,
  validatePagarmeRecipientForm,
  PAGARME_BANK_HOLDER_NAME_MAX,
  type PagarmeRecipientFormErrors,
  type RegisterType,
} from '../../../utils/pagarmeRecipientFormValidation';

const PIX_TYPES = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Chave aleatória' },
] as const;

type Props = {
  onSaved: () => Promise<void> | void;
  /** Recebedor já aprovado no Pagar.me — só pede chave Pix para saque. */
  mode?: 'full' | 'pix-only';
};

const PagarmeRecipientSetupForm: React.FC<Props> = ({ onSaved, mode = 'full' }) => {
  const pixOnly = mode === 'pix-only';
  const [loadingBoot, setLoadingBoot] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendencies, setPendencies] = useState<string[]>([]);
  const [onboardingStatus, setOnboardingStatus] = useState<string>('draft');
  const [providerStatus, setProviderStatus] = useState<string | null>(null);
  const [providerStatusReason, setProviderStatusReason] = useState<string | null>(null);
  const [lastSyncError, setLastSyncError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<PagarmeRecipientFormErrors>({});
  const [hadPixConfigured, setHadPixConfigured] = useState(false);

  const [parishName, setParishName] = useState('');
  const [parishCpfCnpjType, setParishCpfCnpjType] = useState<'cpf' | 'cnpj' | null>(null);

  const [pixKeyType, setPixKeyType] = useState<(typeof PIX_TYPES)[number]['value']>('email');
  const [pixKey, setPixKey] = useState('');

  const [registerType, setRegisterType] = useState<RegisterType>('individual');
  const [birthdate, setBirthdate] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [annualRevenue, setAnnualRevenue] = useState('');
  const [foundingDate, setFoundingDate] = useState('');
  const [sameRepAsHolder, setSameRepAsHolder] = useState(true);
  const [repName, setRepName] = useState('');
  const [repEmail, setRepEmail] = useState('');
  const [repDocument, setRepDocument] = useState('');
  const [repBirthdate, setRepBirthdate] = useState('');
  const [repPhone, setRepPhone] = useState('');

  const [holderName, setHolderName] = useState('');
  const [parishPrincipalDoc, setParishPrincipalDoc] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [branchNumber, setBranchNumber] = useState('');
  const [branchCheckDigit, setBranchCheckDigit] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountCheckDigit, setAccountCheckDigit] = useState('');
  const [accountKind, setAccountKind] = useState<'checking' | 'savings'>('checking');

  const [streetExtra, setStreetExtra] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [neighborhoodExtra, setNeighborhoodExtra] = useState('');
  const [cepExtra, setCepExtra] = useState('');
  const [refPoint, setRefPoint] = useState('');
  const parishDocDigits = useMemo(() => cleanDocument(parishPrincipalDoc), [parishPrincipalDoc]);
  const holderDocumentSynced = useMemo(() => {
    const d = parishDocDigits;
    if (d.length === 11 || d.length === 14) return formatCpfCnpjInput(d);
    return '';
  }, [parishDocDigits]);
  const holderDocDigits = parishDocDigits;
  const holderLooksCnpj = holderDocDigits.length === 14;
  const canCopyHolderAsRepresentative = holderDocDigits.length === 11;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingBoot(true);
      setError(null);
      try {
        const [rx, wd] = await Promise.all([
          withdrawalService.getPagarmeRecipientProfile() as Promise<Record<string, any>>,
          withdrawalService.getWithdrawalData(),
        ]);
        if (cancelled) return;

        const basics = (rx?.parishBasics || {}) as Record<string, string>;
        setParishName(String(basics.name || ''));
        setParishCpfCnpjType((basics.cpfCnpjType as 'cpf' | 'cnpj' | undefined) || null);
        setParishPrincipalDoc(String(basics.cpfCnpj || ''));

        const prof = rx?.recipientProfile || {};
        const inferredType = inferRegisterTypeFromDocument(cleanDocument(String(basics.cpfCnpj || '')));
        if (prof.registerType === 'individual' || prof.registerType === 'corporation') {
          setRegisterType(prof.registerType);
        } else if (inferredType) {
          setRegisterType(inferredType);
        }
        const ind = prof.individual || {};
        const corp = prof.corporation || {};
        const rep = corp.representative || {};
        const ba = prof.bankAccount || {};
        const addr = prof.addressOverride || {};

        setBirthdate(ind.birthdate || '');

        setAnnualRevenue(corp.annualRevenue || '');
        setFoundingDate(corp.foundingDate || '');
        setRepName(rep.name || '');
        setRepEmail(rep.email || '');
        setRepDocument(rep.document || '');
        setRepBirthdate(rep.birthdate || '');
        setRepPhone(rep.phone || '');

        const hasRepIdentity = !!(rep.name?.trim() || rep.document?.trim());
        setSameRepAsHolder(!hasRepIdentity);

        const loadedHolder = (ba.holderName || '').trim();
        const loadedCompany = (corp.companyName || '').trim();
        const loadedTrading = (corp.tradingName || '').trim();
        const parishDocLen = cleanDocument(String(basics.cpfCnpj || '')).length;

        if (parishDocLen === 14) {
          const fallbackCorp = (loadedCompany || loadedTrading || basics.name || '').trim();
          const resolved = resolveCorporateHolderName({
            holderName: loadedHolder,
            companyName: loadedCompany,
            tradingName: loadedTrading,
            parishName: String(basics.name || ''),
          });
          const corpName = resolved || fallbackCorp;
          setCompanyName(corpName);
          setTradingName(loadedTrading || corpName);
          setHolderName(
            loadedHolder && loadedHolder.length <= PAGARME_BANK_HOLDER_NAME_MAX
              ? loadedHolder
              : corpName.slice(0, PAGARME_BANK_HOLDER_NAME_MAX)
          );
        } else {
          setCompanyName(loadedCompany);
          setTradingName(loadedTrading);
          setHolderName(loadedHolder);
        }
        setBankCode(ba.bank || '');
        setBranchNumber(ba.branchNumber || '');
        setBranchCheckDigit(String(ba.branchCheckDigit ?? ''));
        setAccountNumber(ba.accountNumber || '');
        setAccountCheckDigit(String(ba.accountCheckDigit ?? ''));
        if (ba.type === 'savings') setAccountKind('savings');

        setStreetExtra(addr.street || '');
        setStreetNumber(addr.streetNumber || '');
        setNeighborhoodExtra(addr.neighborhood || '');
        setCepExtra(addr.zipCode || '');
        setRefPoint(addr.referencePoint || '');

        setHadPixConfigured(!!wd.pixKeyMasked);
        setPixKey('');
        const pxt = wd.pixKeyType as typeof pixKeyType | undefined;
        if (pxt && PIX_TYPES.some((t) => t.value === pxt)) setPixKeyType(pxt);

        setOnboardingStatus(prof.onboardingStatus || wd.recipientOnboardingStatus || 'draft');
        setProviderStatus(prof.providerStatus || null);
        setProviderStatusReason(prof.providerStatusReason || null);
        setLastSyncError(prof.lastSyncError || null);
        setPendencies((rx.pendencies as string[]) || wd.recipientPendencies || []);
      } catch (e: any) {
        if (!cancelled) setError(e.response?.data?.message || 'Falha ao carregar formulário.');
      } finally {
        if (!cancelled) setLoadingBoot(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (registerType === 'corporation' && sameRepAsHolder && !canCopyHolderAsRepresentative) {
      setSameRepAsHolder(false);
    }
  }, [registerType, sameRepAsHolder, canCopyHolderAsRepresentative]);

  const suggestedHolderName = useMemo(
    () => suggestCompanyHolderName({ companyName, tradingName, parishName }),
    [companyName, tradingName, parishName]
  );

  const effectiveCorporateHolder = useMemo(
    () => resolveCorporateHolderName({ holderName, companyName, tradingName, parishName }),
    [holderName, companyName, tradingName, parishName]
  );

  const holderNameMismatch = Boolean(
    registerType === 'corporation' &&
      holderLooksCnpj &&
      effectiveCorporateHolder &&
      looksLikePersonName(effectiveCorporateHolder)
  );

  const applyCorporateHolder = (name: string) => {
    setCompanyName(name);
    setHolderName(name.trim().slice(0, PAGARME_BANK_HOLDER_NAME_MAX));
    setFieldErrors((prev) => ({ ...prev, holderName: undefined, companyName: undefined }));
  };

  const buildValidationInput = () => ({
    registerType,
    parishDocDigits,
    parishCpfCnpjType,
    parishName,
    holderName:
      registerType === 'corporation'
        ? holderName.trim() || companyName.trim().slice(0, PAGARME_BANK_HOLDER_NAME_MAX)
        : holderName,
    companyName,
    tradingName,
    birthdate,
    repBirthdate,
    repName,
    repEmail,
    repDocument,
    sameRepAsHolder,
    bankCode,
    branchNumber,
    accountNumber,
    accountCheckDigit,
  });

  const runClientValidation = (): boolean => {
    const { errors, blockingMessages } = validatePagarmeRecipientForm(buildValidationInput());
    setFieldErrors(errors);
    if (blockingMessages.length) {
      setError(blockingMessages[0]);
      return false;
    }
    setError(null);
    return true;
  };

  const applyProfileResponse = (prof?: Record<string, any>) => {
    if (!prof) return;
    if (prof.onboardingStatus) setOnboardingStatus(String(prof.onboardingStatus));
    setProviderStatus(prof.providerStatus || null);
    setProviderStatusReason(prof.providerStatusReason || null);
    setLastSyncError(prof.lastSyncError || null);
  };

  const persistProfileParts = (): Record<string, unknown> => {
    const bankHolderName =
      registerType === 'corporation'
        ? holderName.trim() || companyName.trim().slice(0, PAGARME_BANK_HOLDER_NAME_MAX)
        : holderName.trim();
    const corpName = companyName.trim() || bankHolderName;

    return {
      registerType,
      individual: {
        birthdate,
      },
      corporation: {
        companyName: registerType === 'corporation' ? corpName : companyName,
        tradingName: tradingName.trim() || corpName,
        annualRevenue,
        foundingDate: foundingDate.trim() ? foundingDate : undefined,
        representative: {
          name: repName,
          email: repEmail,
          document: repDocument,
          birthdate: repBirthdate,
          phone: repPhone.trim() ? repPhone : undefined,
          selfDeclaredRepresentative: true,
        },
      },
      bankAccount: {
        holderName: bankHolderName,
        holderDocument: holderDocumentSynced,
        bank: bankCode,
        branchNumber,
        branchCheckDigit,
        accountNumber,
        accountCheckDigit,
        type: accountKind,
      },
      addressOverride:
        streetExtra.trim() ||
        streetNumber.trim() ||
        neighborhoodExtra.trim() ||
        cepExtra.trim() ||
        refPoint.trim()
          ? {
              street: streetExtra || undefined,
              streetNumber: streetNumber || undefined,
              neighborhood: neighborhoodExtra || undefined,
              zipCode: cepExtra || undefined,
              referencePoint: refPoint || undefined,
            }
          : undefined,
    };
  };

  const handleSaveAll = async () => {
    if (!runClientValidation()) return;
    setBusy(true);
    setError(null);
    try {
      const putRes = (await withdrawalService.savePagarmeRecipientProfile(persistProfileParts() as any)) as {
        success?: boolean;
        data?: { pendencies?: string[] };
      };
      const pend = putRes?.data?.pendencies;
      setPendencies(Array.isArray(pend) ? (pend as string[]) : []);

      const keyTrim = pixKey.trim();
      if (keyTrim || hadPixConfigured) {
        await withdrawalService.saveWithdrawalData({ pixKeyType, pixKey: keyTrim });
      }

      const syncPayload = (await withdrawalService.syncPagarmeRecipient({ dryRun: false })) as {
        success?: boolean;
        message?: string;
        data?: { recipientProfile?: { onboardingStatus?: string }; pendencies?: string[] };
      };
      const pdata = syncPayload.data;

      applyProfileResponse(pdata?.recipientProfile as Record<string, any> | undefined);
      const pend2 = pdata?.pendencies;
      if (Array.isArray(pend2)) setPendencies(pend2 as string[]);

      if (!syncPayload.success) {
        throw new Error(syncPayload.message || 'Erro ao concluir o cadastro.');
      }

      await onSaved();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Erro ao salvar/sincronizar');
    } finally {
      setBusy(false);
    }
  };

  const handleDryValidate = async () => {
    if (!runClientValidation()) return;
    setBusy(true);
    setError(null);
    try {
      await withdrawalService.savePagarmeRecipientProfile(persistProfileParts() as any);
      const rs = (await withdrawalService.syncPagarmeRecipient({ dryRun: true })) as {
        success?: boolean;
        message?: string;
        data?: { recipientProfile?: { onboardingStatus?: string }; pendencies?: string[] };
      };
      if (!rs.success) {
        setError(rs.message || 'Ainda há pendências no cadastro.');
        const pe = rs.data?.pendencies;
        if (Array.isArray(pe)) setPendencies(pe);
        return;
      }
      const pdata = rs.data;
      applyProfileResponse(pdata?.recipientProfile as Record<string, any> | undefined);
      const pend = pdata?.pendencies;
      if (Array.isArray(pend)) setPendencies(pend);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Erro na validação');
    } finally {
      setBusy(false);
    }
  };

  const handleSavePixOnly = async () => {
    const keyTrim = pixKey.trim();
    if (!keyTrim && !hadPixConfigured) {
      setError('Informe a chave Pix para solicitar saque.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await withdrawalService.saveWithdrawalData({ pixKeyType, pixKey: keyTrim });
      await onSaved();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Erro ao salvar chave Pix');
    } finally {
      setBusy(false);
    }
  };

  if (loadingBoot) {
    return (
      <Box py={6} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (pixOnly) {
    return (
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 520 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Chave Pix para saque
        </Typography>
        <Alert severity="success" sx={{ mb: 2 }}>
          Seu cadastro para receber repasses já está aprovado. Não é necessário repetir banco, CPF/CNPJ ou data de
          nascimento — informe apenas a chave Pix abaixo.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Informe apenas a chave Pix para onde a paróquia deseja receber os saques solicitados no painel.
        </Typography>
        {!!error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Tipo de chave Pix"
            value={pixKeyType}
            onChange={(e) => setPixKeyType(e.target.value as typeof pixKeyType)}
            size="small"
          >
            {PIX_TYPES.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={PIX_TYPES.find((o) => o.value === pixKeyType)?.label || 'Chave Pix'}
            value={pixKey}
            onChange={(e) => setPixKey(e.target.value)}
            size="small"
            required={!hadPixConfigured}
            helperText={
              hadPixConfigured && !pixKey.trim()
                ? 'Chave já cadastrada — deixe vazio para manter ao salvar.'
                : 'Obrigatória para liberar a tela de saque.'
            }
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => handleSavePixOnly()}
            startIcon={busy ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {busy ? 'Salvando…' : 'Salvar chave Pix e continuar'}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h6" gutterBottom color="primary">
        Cadastro para receber repasses
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Poucas perguntas para liberar repasses e saques. Com <strong>CNPJ</strong>, razão social e representante legal
        são <strong>obrigatórios</strong>. A chave Pix só é necessária quando for solicitar saque.
      </Typography>

      {pendencies?.length ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={700}>
            Pendências
          </Typography>
          <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
            {pendencies.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {onboardingStatus === 'ready' ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Cadastro aprovado — repasses e saques liberados nesta plataforma.
        </Alert>
      ) : null}

      {onboardingStatus === 'pending_sync' ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cadastro enviado e em análise
          {providerStatus ? ` (${providerStatus})` : ''}. Isso pode levar alguns minutos. Você pode fechar esta página e
          voltar depois.
        </Alert>
      ) : null}

      {(onboardingStatus === 'error' || lastSyncError) && onboardingStatus !== 'ready' ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={700} gutterBottom>
            Cadastro recusado ou com erro
          </Typography>
          <Typography variant="body2">
            {lastSyncError || providerStatusReason || 'Revise os dados abaixo e tente novamente.'}
          </Typography>
          {holderLooksCnpj ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Dica: com CNPJ, o titular da conta deve ser a <strong>razão social</strong> (como no extrato bancário), e o
              representante legal deve ser uma pessoa com CPF válido.
            </Typography>
          ) : null}
        </Alert>
      ) : null}

      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
        Tipo de recebedor
      </Typography>
      {fieldErrors.registerType ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {fieldErrors.registerType}
        </Alert>
      ) : null}
      {holderLooksCnpj ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          Seu cadastro usa <strong>CNPJ</strong> — escolha Pessoa jurídica e preencha o titular da conta com a razão social
          da paróquia/entidade.
        </Alert>
      ) : parishDocDigits.length === 11 ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          Seu cadastro usa <strong>CPF</strong> — escolha Pessoa física e use o nome completo do titular como está no banco.
        </Alert>
      ) : null}
      <ToggleButtonGroup
        size="small"
        color="primary"
        value={registerType}
        exclusive
        onChange={(_, v: RegisterType | null) => {
          if (v) {
            setRegisterType(v);
            setFieldErrors((prev) => ({ ...prev, registerType: undefined }));
          }
        }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="corporation">Pessoa jurídica (CNPJ)</ToggleButton>
        <ToggleButton value="individual">Pessoa física (CPF)</ToggleButton>
      </ToggleButtonGroup>

      {registerType === 'corporation' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Alert severity="info">
            Com CNPJ, o titular da conta no banco deve ser a <strong>razão social</strong> da paróquia/entidade — não o
            nome do padre ou responsável. O representante legal (CPF) é informado mais abaixo.
          </Alert>
          {holderNameMismatch ? (
            <Alert severity="warning">
              Ainda há um nome de pessoa no titular. Corrija a razão social abaixo ou{' '}
              {suggestedHolderName ? (
                <Button size="small" sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline' }} onClick={() => applyCorporateHolder(suggestedHolderName)}>
                  usar: {suggestedHolderName}
                </Button>
              ) : (
                'preencha a razão social exatamente como no extrato bancário.'
              )}
            </Alert>
          ) : null}
          <TextField
            label="Razão social *"
            value={companyName}
            onChange={(e) => applyCorporateHolder(e.target.value)}
            size="small"
            required
            error={!!fieldErrors.companyName || holderNameMismatch}
            helperText={
              fieldErrors.companyName ||
              'Nome completo da empresa (CNPJ). Encurte o titular do banco no campo abaixo (máx. 30).'
            }
          />
          <TextField
            label="Nome fantasia (opcional)"
            value={tradingName}
            onChange={(e) => setTradingName(e.target.value)}
            size="small"
            helperText="Se vazio, usamos a razão social."
          />
        </Box>
      ) : null}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
        Conta bancária (repasses)
      </Typography>
      {holderNameMismatch && registerType !== 'corporation' ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          O titular parece ser nome de pessoa, mas o documento é CNPJ. Use a razão social exatamente como está no banco.
          {suggestedHolderName ? (
            <>
              {' '}
              <Button size="small" sx={{ ml: 0.5, p: 0, minWidth: 0 }} onClick={() => applyCorporateHolder(suggestedHolderName)}>
                Usar sugestão: {suggestedHolderName}
              </Button>
            </>
          ) : null}
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        {registerType !== 'corporation' ? (
          <TextField
            label="Nome completo do titular (como no banco)"
            value={holderName}
            onChange={(e) => setHolderName(e.target.value.slice(0, PAGARME_BANK_HOLDER_NAME_MAX))}
            size="small"
            required
            inputProps={{ maxLength: PAGARME_BANK_HOLDER_NAME_MAX }}
            error={!!fieldErrors.holderName}
            helperText={
              fieldErrors.holderName ||
              `Nome completo igual ao cadastro bancário da conta PF. ${holderName.length}/${PAGARME_BANK_HOLDER_NAME_MAX}`
            }
          />
        ) : (
          <TextField
            label="Titular enviado ao banco"
            value={holderName}
            onChange={(e) => {
              setHolderName(e.target.value.slice(0, PAGARME_BANK_HOLDER_NAME_MAX));
              setFieldErrors((prev) => ({ ...prev, holderName: undefined }));
            }}
            size="small"
            required
            inputProps={{ maxLength: PAGARME_BANK_HOLDER_NAME_MAX }}
            error={!!fieldErrors.holderName}
            helperText={
              fieldErrors.holderName ||
              `Nome curto enviado ao Pagar.me (máx. ${PAGARME_BANK_HOLDER_NAME_MAX}). ${holderName.length}/${PAGARME_BANK_HOLDER_NAME_MAX}`
            }
          />
        )}
        <TextField
          label="CPF/CNPJ do titular"
          value={holderDocumentSynced}
          size="small"
          InputProps={{ readOnly: true }}
          error={!!fieldErrors.parishDoc}
          helperText={
            fieldErrors.parishDoc ||
            'Igual ao CPF/CNPJ do cadastro principal da paróquia. Altere em Configurações / dados cadastrais.'
          }
        />
        <TextField
          label="Código do banco"
          value={bankCode}
          onChange={(e) => setBankCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
          size="small"
          required
          error={!!fieldErrors.bankCode}
          helperText={fieldErrors.bankCode || '3 dígitos — ex.: 341 Itaú, 033 Santander, 077 Inter, 260 Nubank'}
        />
        <TextField
          label="Agência"
          value={branchNumber}
          onChange={(e) => setBranchNumber(e.target.value)}
          size="small"
          required
          error={!!fieldErrors.branchNumber}
          helperText={fieldErrors.branchNumber}
        />
        <TextField
          label="Dígito agência (se houver)"
          value={branchCheckDigit}
          onChange={(e) => setBranchCheckDigit(e.target.value)}
          size="small"
          helperText="Inter (077) e alguns bancos exigem dígito da agência — confira no app do banco."
        />
        <TextField
          label="Conta"
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
          size="small"
          required
          error={!!fieldErrors.accountNumber}
          helperText={fieldErrors.accountNumber}
        />
        <TextField
          label="Dígito da conta"
          value={accountCheckDigit}
          onChange={(e) => setAccountCheckDigit(e.target.value)}
          size="small"
          required
          error={!!fieldErrors.accountCheckDigit}
          helperText={fieldErrors.accountCheckDigit || 'Pode ser 0 ou X, conforme seu banco.'}
        />
        <TextField
          select
          label="Tipo de conta"
          value={accountKind}
          onChange={(e) => setAccountKind(e.target.value as 'checking' | 'savings')}
          size="small"
        >
          <MenuItem value="checking">Corrente</MenuItem>
          <MenuItem value="savings">Poupança</MenuItem>
        </TextField>
      </Box>

      {registerType === 'individual' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Alert severity="info">
            CPF do recebedor vem do cadastro da paróquia. O nome do titular da conta (ou da paróquia) será usado no
            cadastro. Outros dados PF usam valores padrão se você não informar no painel admin.
          </Alert>
          <TextField
            label="Data de nascimento do titular (DD/MM/AAAA)"
            value={birthdate}
            onChange={(e) => setBirthdate(e.target.value)}
            size="small"
            required
            error={!!fieldErrors.birthdate}
            helperText={fieldErrors.birthdate}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Representante legal (PJ) *
          </Typography>
          <Alert severity="info">
            Com CNPJ, informe a <strong>pessoa física</strong> que assina pela entidade (nome, CPF, e-mail e
            nascimento). Isso é diferente da razão social do titular da conta.
          </Alert>
          <TextField
            label="Nome do representante *"
            value={repName}
            onChange={(e) => setRepName(e.target.value)}
            size="small"
            required
            error={!!fieldErrors.repName}
            helperText={fieldErrors.repName}
          />
          <TextField
            label="E-mail do representante *"
            value={repEmail}
            onChange={(e) => setRepEmail(e.target.value)}
            size="small"
            required
            error={!!fieldErrors.repEmail}
            helperText={fieldErrors.repEmail}
          />
          <TextField
            label="CPF do representante *"
            value={repDocument}
            onChange={(e) => setRepDocument(e.target.value)}
            size="small"
            required
            error={!!fieldErrors.repDocument}
            helperText={fieldErrors.repDocument || 'CPF da pessoa que assina pela paróquia/entidade.'}
          />
          <TextField label="Telefone do representante (opcional)" value={repPhone} onChange={(e) => setRepPhone(e.target.value)} size="small" />
          <TextField
            label="Nascimento do representante (DD/MM/AAAA) *"
            value={repBirthdate}
            onChange={(e) => setRepBirthdate(e.target.value)}
            size="small"
            required
            error={!!fieldErrors.repBirthdate}
            helperText={fieldErrors.repBirthdate}
          />

          <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Dados extras da empresa (opcional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Faturamento anual (centavos)"
                  helperText="Em branco: valor padrão interno"
                  value={annualRevenue}
                  onChange={(e) => setAnnualRevenue(e.target.value.replace(/\D/g, ''))}
                  size="small"
                />
                <TextField label="Data de fundação (DD/MM/AAAA)" value={foundingDate} onChange={(e) => setFoundingDate(e.target.value)} size="small" />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      <Accordion disableGutters elevation={0} sx={{ mb: 2, border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Endereço extra (opcional)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Se vazio, usamos cidade/UF/CEP do cadastro da paróquia e completamos complemento/referência com padrão para a API.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Logradouro" value={streetExtra} onChange={(e) => setStreetExtra(e.target.value)} size="small" />
            <TextField label="Número" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} size="small" />
            <TextField label="Bairro" value={neighborhoodExtra} onChange={(e) => setNeighborhoodExtra(e.target.value)} size="small" />
            <TextField label="CEP (somente dígitos)" value={cepExtra} onChange={(e) => setCepExtra(e.target.value)} size="small" />
            <TextField label="Ponto de referência" value={refPoint} onChange={(e) => setRefPoint(e.target.value)} size="small" />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters elevation={0} sx={{ mb: 2, border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Chave Pix — só para solicitar saque</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 2 }}>
            Você pode concluir o cadastro sem Pix. Informe a chave quando for pedir saque; até lá pode aparecer como
            pendência, sem impedir o cadastro.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              label="Tipo de chave PIX"
              value={pixKeyType}
              onChange={(e) => setPixKeyType(e.target.value as any)}
              size="small"
            >
              {PIX_TYPES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={PIX_TYPES.find((o) => o.value === pixKeyType)?.label || 'Chave PIX'}
              value={pixKey}
              onChange={(e) => setPixKey(e.target.value)}
              size="small"
              helperText={
                hadPixConfigured && !pixKey.trim()
                  ? 'Chave já cadastrada — deixe vazio para manter ao salvar.'
                  : undefined
              }
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button variant="outlined" disabled={busy} onClick={() => handleDryValidate()}>
          Verificar dados
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={() => handleSaveAll()}
          startIcon={busy ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {busy ? 'Processando…' : 'Salvar e concluir cadastro'}
        </Button>
      </Box>
    </Paper>
  );
};

export default PagarmeRecipientSetupForm;
